import { entrySplitApi as api } from "../entryApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    usersControllerCreate: build.mutation<
      UsersControllerCreateApiResponse,
      UsersControllerCreateApiArg
    >({
      query: (queryArg) => ({
        url: `/users`,
        method: "POST",
        body: queryArg.createUserDto,
      }),
    }),
    usersControllerList: build.query<
      UsersControllerListApiResponse,
      UsersControllerListApiArg
    >({
      query: (queryArg) => ({
        url: `/users`,
        params: {
          page: queryArg.page,
          pageSize: queryArg.pageSize,
          orderBy: queryArg.orderBy,
          sortOrder: queryArg.sortOrder,
          keyword: queryArg.keyword,
          name: queryArg.name,
          role: queryArg.role,
        },
      }),
    }),
    usersControllerPermissions: build.query<
      UsersControllerPermissionsApiResponse,
      UsersControllerPermissionsApiArg
    >({
      query: () => ({ url: `/users/permissions` }),
    }),
    usersControllerUpdateDefaultLanguage: build.mutation<
      UsersControllerUpdateDefaultLanguageApiResponse,
      UsersControllerUpdateDefaultLanguageApiArg
    >({
      query: (queryArg) => ({
        url: `/users/defaultLanguage`,
        method: "PATCH",
        body: queryArg.updateDefaultLanguageDto,
      }),
    }),
    usersControllerFindById: build.query<
      UsersControllerFindByIdApiResponse,
      UsersControllerFindByIdApiArg
    >({
      query: (queryArg) => ({ url: `/users/${queryArg.id}` }),
    }),
    usersControllerUpdate: build.mutation<
      UsersControllerUpdateApiResponse,
      UsersControllerUpdateApiArg
    >({
      query: (queryArg) => ({
        url: `/users/${queryArg.id}`,
        method: "PATCH",
        body: queryArg.updateUserDto,
      }),
    }),
    usersControllerRemove: build.mutation<
      UsersControllerRemoveApiResponse,
      UsersControllerRemoveApiArg
    >({
      query: (queryArg) => ({ url: `/users/${queryArg.id}`, method: "DELETE" }),
    }),
    usersControllerUpdateRole: build.mutation<
      UsersControllerUpdateRoleApiResponse,
      UsersControllerUpdateRoleApiArg
    >({
      query: (queryArg) => ({
        url: `/users/role`,
        method: "PATCH",
        body: queryArg.updateUserRoleDto,
      }),
    }),
    usersControllerResetPassword: build.mutation<
      UsersControllerResetPasswordApiResponse,
      UsersControllerResetPasswordApiArg
    >({
      query: (queryArg) => ({
        url: `/users/resetPassword/${queryArg.id}`,
        method: "PATCH",
      }),
    }),
    usersControllerUpdatePassword: build.mutation<
      UsersControllerUpdatePasswordApiResponse,
      UsersControllerUpdatePasswordApiArg
    >({
      query: (queryArg) => ({
        url: `/users/updatePassword/${queryArg.id}`,
        method: "PATCH",
        body: queryArg.updatePasswordDto,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as combinedApi };
export type UsersControllerCreateApiResponse = unknown;
export type UsersControllerCreateApiArg = {
  createUserDto: CreateUserDto;
};
export type UsersControllerListApiResponse = /** status 200  */ {
  data?: UserDto[];
} & {
  meta?: {
    total?: number;
    lastPage?: number;
    currentPage?: number;
    perPage?: number;
    prev?: number;
    next?: number;
  };
};
export type UsersControllerListApiArg = {
  page: number;
  pageSize: number;
  orderBy?: string;
  sortOrder?: "ASC" | "DESC";
  keyword?: string;
  name?: string;
  role?: string;
};
export type UsersControllerPermissionsApiResponse = /** status 200  */ string[];
export type UsersControllerPermissionsApiArg = void;
export type UsersControllerUpdateDefaultLanguageApiResponse =
  /** status 200  */ UserDto;
export type UsersControllerUpdateDefaultLanguageApiArg = {
  updateDefaultLanguageDto: UpdateDefaultLanguageDto;
};
export type UsersControllerFindByIdApiResponse = /** status 200  */ UserDto;
export type UsersControllerFindByIdApiArg = {
  id: string;
};
export type UsersControllerUpdateApiResponse = /** status 200  */ UserDto;
export type UsersControllerUpdateApiArg = {
  id: string;
  updateUserDto: UpdateUserDto;
};
export type UsersControllerRemoveApiResponse = unknown;
export type UsersControllerRemoveApiArg = {
  id: string;
};
export type UsersControllerUpdateRoleApiResponse = /** status 200  */ UserDto;
export type UsersControllerUpdateRoleApiArg = {
  updateUserRoleDto: UpdateUserRoleDto;
};
export type UsersControllerResetPasswordApiResponse = unknown;
export type UsersControllerResetPasswordApiArg = {
  id: string;
};
export type UsersControllerUpdatePasswordApiResponse = unknown;
export type UsersControllerUpdatePasswordApiArg = {
  id: string;
  updatePasswordDto: UpdatePasswordDto;
};
export type LanguageType = "en" | "cn" | "cnt";
export type CreateUserDto = {
  username: string;
  password: string;
  verificationCode: string;
  invitationCode: string;
  firstName: string;
  lastName: string;
  mobile?: string;
  defaultLanguage?: LanguageType;
};
export type QuestionScoreByTypeDto = {
  decisive: number;
  analytical: number;
  creative: number;
  idealistic: number;
  controlling: number;
  affairs: number;
  feel: number;
  interpersonal: number;
};
export type UserDto = {
  id: string;
  name: string;
  username: string;
  firstName?: string;
  lastName?: string;
  alias?: string;
  mobile?: string;
  defaultLanguage: LanguageType;
  recordsCount?: number;
  averageScore?: QuestionScoreByTypeDto;
  isSuperAdmin?: boolean;
  role?: string;
  roleId?: string;
  roleExpiredBy?: string;
  createdAt: string;
  updatedAt: string;
};
export type UpdateDefaultLanguageDto = {
  id: string;
  defaultLanguage: LanguageType;
};
export type UpdateUserDto = {
  username?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  alias?: string;
  defaultLanguage?: LanguageType;
};
export type UpdateUserRoleDto = {
  userId: string;
  roleId: string;
  expiredBy: string;
};
export type UpdatePasswordDto = {
  password: string;
};
export const {
  useUsersControllerCreateMutation,
  useUsersControllerListQuery,
  useLazyUsersControllerListQuery,
  useUsersControllerPermissionsQuery,
  useLazyUsersControllerPermissionsQuery,
  useUsersControllerUpdateDefaultLanguageMutation,
  useUsersControllerFindByIdQuery,
  useLazyUsersControllerFindByIdQuery,
  useUsersControllerUpdateMutation,
  useUsersControllerRemoveMutation,
  useUsersControllerUpdateRoleMutation,
  useUsersControllerResetPasswordMutation,
  useUsersControllerUpdatePasswordMutation,
} = injectedRtkApi;
